import React from 'react';
import {
  VictoryChart,
  VictoryBar,
  VictoryStack,
  VictoryAxis,
  VictoryTheme,
  VictoryLegend,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from 'victory';
import { formatPeriodES } from '../../../config/reportTranslations';
import './Charts.css';
import { REPORT_CHART_COLORS } from '../../../constants/colors';

/**
 * Gráfico 10: Eficiencia y Gestión
 *
 * Stacked bar chart showing income breakdown:
 * - Costo (Interest expense as % of income) - Dark gray solid
 * - Gasto (Operating expense as % of income) - Teal solid
 * - Provisión (Provisions as % of income) - Dark gray with white stripes
 * - Margen Intermediación (Intermediation margin) - Teal with white stripes
 *
 * Supports negative margins which appear below the 0% axis line.
 * Matches PDF report format from Atuntaqui_mar2025
 */

export interface Grafico10V2Data {
  period: string;              // YYYY-MM format
  costo_pct: number;           // Interest expense as % of income (e.g., 0.62 = 62%)
  gasto_pct: number;           // Operating expense as % of income
  provision_pct: number;       // Provisions as % of income
  margen_intermediacion: number; // Intermediation margin (can be negative)
}

export interface Grafico10V2Props {
  data: Grafico10V2Data[];
  title?: string;
}

const COLORS = REPORT_CHART_COLORS;

// SVG Pattern definitions for striped bars (matching PDF report style)
const StripedPatterns: React.FC = () => (
  <svg style={{ position: 'absolute', width: 0, height: 0 }}>
    <defs>
      {/* Dark gray with white diagonal stripes - rotate(-45) for opposite diagonal */}
      <pattern
        id="stripedPrimaryG10"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(-45)"
      >
        <rect width="4" height="4" fill={COLORS.primary} />
        <line x1="0" y1="0" x2="0" y2="4" stroke="white" strokeWidth="2" />
      </pattern>
      {/* Teal with white diagonal stripes - rotate(-45) for opposite diagonal */}
      <pattern
        id="stripedSecondaryG10"
        patternUnits="userSpaceOnUse"
        width="4"
        height="4"
        patternTransform="rotate(-45)"
      >
        <rect width="4" height="4" fill={COLORS.secondary} />
        <line x1="0" y1="0" x2="0" y2="4" stroke="white" strokeWidth="2" />
      </pattern>
    </defs>
  </svg>
);

export const Grafico10V2: React.FC<Grafico10V2Props> = ({
  data,
  title = 'Gráfico 10: Eficiencia y Gestión'
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="chart-container">
        <h4 className="chart-title">{title}</h4>
        <p className="chart-no-data">No hay datos disponibles para este período</p>
      </div>
    );
  }

  // Sort data by period (oldest first)
  const sortedData = [...data].sort((a, b) => a.period.localeCompare(b.period));

  // Transform data for stacked bars (as percentages)
  // Stack order from bottom: Costo, Gasto, Provisión, Margen (when positive)
  const costoData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.costo_pct * 100,
  }));

  const gastoData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.gasto_pct * 100,
  }));

  const provisionData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: d.provision_pct * 100,
  }));

  // Positive margin goes in the stack (above 0)
  const margenPositiveData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: Math.max(0, d.margen_intermediacion * 100),
  }));

  // Negative margin is a separate bar below 0
  const margenNegativeData = sortedData.map(d => ({
    x: formatPeriodES(d.period),
    y: Math.min(0, d.margen_intermediacion * 100),
  }));

  // Check if we have any negative margins
  const hasNegativeMargin = sortedData.some(d => d.margen_intermediacion < 0);

  // Calculate Y domain - include negative values if present
  const maxStack = Math.max(...sortedData.map(d =>
    (d.costo_pct + d.gasto_pct + d.provision_pct + Math.max(0, d.margen_intermediacion)) * 100
  ));
  const minMargin = Math.min(...sortedData.map(d => d.margen_intermediacion * 100));

  const yMax = Math.ceil(Math.max(maxStack * 1.1, 120) / 20) * 20; // Round to nearest 20
  const yMin = hasNegativeMargin ? Math.floor(minMargin * 1.2 / 20) * 20 : 0; // Round to nearest 20

  return (
    <div className="chart-container">
      {/* SVG pattern definitions */}
      <StripedPatterns />

      <h4 className="chart-title">{title}</h4>

      <div className="chart-wrapper">
        <VictoryChart
          theme={VictoryTheme.material}
          domainPadding={{ x: 25, y: 10 }}
          padding={{ top: 50, bottom: 60, left: 55, right: 20 }}
          height={320}
          containerComponent={
            <VictoryVoronoiContainer
              labels={({ datum }) => datum.label}
              labelComponent={<VictoryTooltip style={{ fontSize: 9 }} />}
            />
          }
        >
          {/* Legend */}
          <VictoryLegend
            x={20}
            y={5}
            orientation="horizontal"
            gutter={12}
            style={{ labels: { fontSize: 7 } }}
            data={[
              { name: 'Costo', symbol: { fill: COLORS.primary } },
              { name: 'Gasto', symbol: { fill: COLORS.secondary } },
              { name: 'Provisión', symbol: { fill: COLORS.primary, opacity: 0.5 } },
              { name: 'Margen', symbol: { fill: COLORS.secondary, opacity: 0.5 } },
            ]}
          />

          {/* X-Axis at y=0 */}
          <VictoryAxis
            style={{
              axis: { stroke: '#666' },
              tickLabels: {
                fontSize: 9,
                padding: 5,
                angle: -45,
                textAnchor: 'end'
              }
            }}
          />

          {/* Y-Axis with domain including negative values */}
          <VictoryAxis
            dependentAxis
            domain={[yMin, yMax]}
            tickFormat={(t: number) => `${t.toFixed(0)}%`}
            style={{
              axis: { stroke: '#666' },
              tickLabels: { fontSize: 9, padding: 5 },
              grid: { stroke: '#e0e0e0', strokeDasharray: '3,3' }
            }}
          />

          {/* Negative margin bars (below 0 line) - rendered first */}
          {hasNegativeMargin && (
            <VictoryBar
              data={margenNegativeData}
              barWidth={12}
              style={{ data: { fill: 'url(#stripedSecondaryG10)' } }}
            />
          )}

          {/* Stacked Bars - 4 segments with solid and striped patterns */}
          <VictoryStack>
            {/* 1. Costo - solid primary (dark gray) */}
            <VictoryBar
              data={costoData}
              barWidth={12}
              style={{ data: { fill: COLORS.primary } }}
            />
            {/* 2. Gasto - solid secondary (teal) */}
            <VictoryBar
              data={gastoData}
              barWidth={12}
              style={{ data: { fill: COLORS.secondary } }}
            />
            {/* 3. Provisión - striped primary (dark gray with white stripes) */}
            <VictoryBar
              data={provisionData}
              barWidth={12}
              style={{ data: { fill: 'url(#stripedPrimaryG10)' } }}
            />
            {/* 4. Margen (positive only) - striped secondary (teal with white stripes) */}
            <VictoryBar
              data={margenPositiveData}
              barWidth={12}
              style={{ data: { fill: 'url(#stripedSecondaryG10)' } }}
            />
          </VictoryStack>
        </VictoryChart>
      </div>

      <p className="chart-source">
        Fuente: COAC / Elaboración: UnionRatings
      </p>
    </div>
  );
};

export default Grafico10V2;
